import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import logger from '@widgets/logger';
import { useTranslation, useQueryParams } from '@hooks';
import { ModalConfirm } from '@components/Modal';
import {
    getGoogleOAuthUrl,
    AccIntegrationName,
    IntegrationSetupType,
    TwoStepIntegrationConfig,
} from '@constants/integrations';
import { ExternalRoutes, RoutePath } from '@constants/routes';
import { CONNECT_WHATSAPP_BUSINESS_ACCOUNT_PARAM, CONNECT_WHATSAPP_BUSINESS_NUMBER_PARAM } from '@constants/channels';
import {
    googleIntegrationService,
    ifoodIntegrationService,
    vtexIntegrationService,
    coverManagerIntegrationService,
    facebookIntegrationService,
    whatsappIntegrationService,
    tiktokIntegrationService,
    xIntegrationService,
    linkedinIntegrationService,
    squareIntegrationService,
    ringcentralIntegrationService,
} from '@services';
import { alertActions, accountActions, googleActions } from '@actions';
import { handleApiError } from '@helpers/api';
import ZapierModal from '@features/ZapierModal';
import FacebookSDK from '@widgets/facebook';
import WhatsAppSDK from '@widgets/whatsapp';
import IFoodModal from './IFoodModal';
import VTEXModal from './VTEXModal';
import CoverManagerModal from './CoverManagerModal';
import ManageIntegrationModal from './ManageIntegrationModal';

const GOOGLE_ERROR = {
    CANCEL: 'access_denied',
};

const useIntegrationActions = () => {
    const { t } = useTranslation();

    const [zapierOpen, setZapierOpen] = useState(false);
    const [iFoodModalOpen, setIFoodModalOpen] = useState(false);
    const [vtexModalOpen, setVtexModalOpen] = useState(false);
    const [coverManagerModalOpen, setCoverManagerModalOpen] = useState(false);
    const [isDisconnectModalOpen, setDisconnectModalOpen] = useState(false);
    const [disconnectType, setDisconnectType] = useState(null);
    const [disconnectIntegrationId, setDisconnectIntegrationId] = useState(null);
    const [manageModalIntegration, setManageModalIntegration] = useState(null);
    const [manageModalIntegrationId, setManageModalIntegrationId] = useState(null);
    const [manageInitialConnect, setManageInitialConnect] = useState(false);

    const account = useSelector(state => state.account?.account);
    const dispatch = useDispatch();
    const query = useQueryParams();
    const navigate = useNavigate();

    const openManageAfterConnect = (type, integrationId = null) => {
        setManageInitialConnect(true);
        setManageModalIntegrationId(integrationId);
        setManageModalIntegration(type);
    };

    const connectGoogleIntegration = async code => {
        try {
            const { integrationId } = await googleIntegrationService.authenticateGoogleIntegration({
                accountId: account?.id,
                code,
                redirectUri: `${window.location.origin}${RoutePath.AccountSettingsIntegrations}`,
            });
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            const locations = await dispatch(googleActions.get(integrationId));
            if (!Array.isArray(locations)) throw new Error('Failed to load Google locations');
            if (locations.length === 0) {
                dispatch(alertActions.error(t('SettingsIntegrations.googleNoLocations')));
                // Revoke the exact account just connected — not an arbitrary Google integration.
                await googleIntegrationService.revokeGoogleIntegration(integrationId);
            } else {
                dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
                dispatch(accountActions.get());
                openManageAfterConnect(AccIntegrationName.Google, integrationId);
            }
        } catch (error) {
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            handleApiError(error, 'alertMessages.connectFail');
        }
    };

    const connectFacebookIntegration = async data => {
        const { accessToken, expiresIn, userID } = data;
        try {
            if (!accessToken) return;
            const result = await facebookIntegrationService.authenticateFacebookIntegration({
                accessToken,
                expiresIn,
                userID,
            });
            dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
            dispatch(accountActions.get());
            openManageAfterConnect(AccIntegrationName.Facebook, result?.integrationId);
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
            if (error.errorCode === 'FACEBOOK_INVALID_TOKEN') {
                logger.error('FACEBOOK_INVALID_TOKEN');
            }
        }
    };

    const connectWhatsAppIntegration = async data => {
        const { code, wabaId: redirectWabaId } = data;
        try {
            if (!code) return;

            const {
                wabaId: sessionWabaId,
                phoneNumberId,
                businessId,
                isOnBusinessApp,
            } = WhatsAppSDK.getEmbeddedSignupSession();
            const wabaId = sessionWabaId || redirectWabaId || '';

            await whatsappIntegrationService.authenticateWhatsAppIntegration({
                code,
                wabaId,
                phoneNumberId,
                businessId,
                redirectUri: null,
            });

            WhatsAppSDK.clearEmbeddedSignupData();

            if (isOnBusinessApp) {
                const query = new URLSearchParams({
                    [CONNECT_WHATSAPP_BUSINESS_NUMBER_PARAM]: phoneNumberId,
                    [CONNECT_WHATSAPP_BUSINESS_ACCOUNT_PARAM]: wabaId,
                });
                navigate(`${RoutePath.AccountSettingsChannels}?${query}`);
            }
            dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
            dispatch(accountActions.get());
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
            logger.error('WhatsApp integration error:', error);
        }
    };

    const connectTikTokIntegration = async ({ code, state }) => {
        try {
            const result = await tiktokIntegrationService.connectIntegration({ code, state });
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
            dispatch(accountActions.get());
            openManageAfterConnect(AccIntegrationName.TikTok, result?.integrationId);
        } catch (error) {
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    const onConnectTikTok = async () => {
        try {
            const { url } = await tiktokIntegrationService.getAuthorizeUrl();
            if (url) {
                window.location = url;
            }
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    const connectXIntegration = async ({ code, state }) => {
        try {
            const result = await xIntegrationService.connectIntegration({ code, state });
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
            dispatch(accountActions.get());
            openManageAfterConnect(AccIntegrationName.X, result?.integrationId);
        } catch (error) {
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    const onConnectX = async () => {
        try {
            const { url } = await xIntegrationService.getAuthorizeUrl();
            if (url) {
                window.location = url;
            }
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    const connectLinkedInIntegration = async ({ code, state }) => {
        try {
            const result = await linkedinIntegrationService.connectIntegration({ code, state });
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
            dispatch(accountActions.get());
            openManageAfterConnect(AccIntegrationName.LinkedIn, result?.integrationId);
        } catch (error) {
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    const onConnectLinkedIn = async () => {
        try {
            const { url } = await linkedinIntegrationService.getAuthorizeUrl();
            if (url) {
                window.location = url;
            }
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    const connectSquareIntegration = async ({ code, state }) => {
        try {
            const result = await squareIntegrationService.connectIntegration({ code, state });
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
            dispatch(accountActions.get());
            openManageAfterConnect(AccIntegrationName.Square, result?.integrationId);
        } catch (error) {
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    const onConnectSquare = async () => {
        try {
            const { url } = await squareIntegrationService.getAuthorizeUrl();
            if (url) {
                window.location = url;
            }
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    const connectRingCentralIntegration = async ({ code, state }) => {
        try {
            await ringcentralIntegrationService.connectIntegration({ code, state });
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            dispatch(alertActions.success(t('alertMessages.connectIntegrationSuccess')));
            dispatch(accountActions.get());
        } catch (error) {
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    const onConnectRingCentral = async () => {
        try {
            const { url } = await ringcentralIntegrationService.getAuthorizeUrl();
            if (url) {
                window.location = url;
            }
        } catch (error) {
            handleApiError(error, 'alertMessages.connectIntegrationFail');
        }
    };

    const onConnectFacebook = async () => {
        try {
            const { status } = await FacebookSDK.getLoginStatus();
            if (!status) return;

            const response = await FacebookSDK.login();
            if (response.authResponse && response.status === 'connected') {
                connectFacebookIntegration(response.authResponse);
            }
        } catch (error) {
            logger.error(error);
        }
    };

    const onConnectWhatsApp = async () => {
        try {
            const { status } = await WhatsAppSDK.getLoginStatus();
            if (!status) return;

            const response = await WhatsAppSDK.loginWhatsApp();

            if (response.code) {
                connectWhatsAppIntegration(response);
            } else if (response.authResponse && response.authResponse.code) {
                connectWhatsAppIntegration(response.authResponse);
            } else if (response.status === 'connected') {
                const urlCode = query.get('code');
                if (urlCode) {
                    connectWhatsAppIntegration({ code: urlCode });
                }
            }
        } catch (error) {
            logger.error('WhatsApp connection error:', error);
        }
    };

    // apiCall receives the specific integration id so accounts with several connected accounts of
    // the same type disconnect exactly one. Types that are single-per-account ignore the argument.
    const IntegrationMapCb = {
        [AccIntegrationName.Google]: {
            apiCall: integrationId => googleIntegrationService.revokeGoogleIntegration(integrationId),
            notConnectedCb: () => (window.location = getGoogleOAuthUrl()),
        },
        [AccIntegrationName.iFood]: {
            apiCall: () => ifoodIntegrationService.disconnectIntegration(),
            notConnectedCb: () => setIFoodModalOpen(true),
        },
        [AccIntegrationName.VTEX]: {
            apiCall: () => vtexIntegrationService.disconnectIntegration(),
            notConnectedCb: () => setVtexModalOpen(true),
        },
        [AccIntegrationName.CoverManager]: {
            apiCall: () => coverManagerIntegrationService.disconnectIntegration(),
            notConnectedCb: () => setCoverManagerModalOpen(true),
        },
        [AccIntegrationName.Facebook]: {
            apiCall: integrationId => facebookIntegrationService.disconnectIntegration(integrationId),
            notConnectedCb: () => onConnectFacebook(),
        },
        [AccIntegrationName.WhatsApp]: {
            apiCall: integrationId => {
                // NOT isIntegrationConnected: a disconnect fallback must target ANY WhatsApp row
                // (incl. inactive) when no explicit id is passed, so it can tear an inactive one down.
                const fallback = account?.integrations.find(i => i.type === AccIntegrationName.WhatsApp)?.id;
                return whatsappIntegrationService.disconnectIntegration(integrationId || fallback);
            },
            notConnectedCb: () => onConnectWhatsApp(),
        },
        [AccIntegrationName.TikTok]: {
            apiCall: integrationId => tiktokIntegrationService.disconnectIntegration(integrationId),
            notConnectedCb: () => onConnectTikTok(),
        },
        [AccIntegrationName.X]: {
            apiCall: integrationId => xIntegrationService.disconnectIntegration(integrationId),
            notConnectedCb: () => onConnectX(),
        },
        [AccIntegrationName.LinkedIn]: {
            apiCall: integrationId => linkedinIntegrationService.disconnectIntegration(integrationId),
            notConnectedCb: () => onConnectLinkedIn(),
        },
        [AccIntegrationName.Square]: {
            apiCall: integrationId => squareIntegrationService.disconnectIntegration(integrationId),
            notConnectedCb: () => onConnectSquare(),
        },
        [AccIntegrationName.RingCentral]: {
            apiCall: integrationId => ringcentralIntegrationService.disconnectIntegration(integrationId),
            notConnectedCb: () => onConnectRingCentral(),
        },
    };

    useEffect(() => {
        const code = query.get('code');
        const error = query.get('error');
        const errorDescription = query.get('error_description');
        const state = query.get('state');
        const wabaId = query.get('waba_id');

        if (error && state && state.startsWith('tt_')) {
            // TikTok rejected the authorize step (user denied, server_error, scope
            // mismatch, sandbox allow-list, etc). Surface the platform-supplied
            // message so the user gets feedback instead of a silently-stranded URL.
            // Must run before the Google access_denied branch — TikTok uses the
            // same error code when the user cancels and we'd otherwise swallow it.
            logger.error('TikTok OAuth error:', { error, errorDescription, state });
            // navigate must run before the alert dispatch — alerts raised before a
            // navigation are cleared once the new location renders, which would wipe
            // an alert dispatched before the redirect.
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            dispatch(alertActions.error(errorDescription || t('alertMessages.connectIntegrationFail')));
        } else if (error && state && state.startsWith('x_')) {
            // X rejected the authorize step. Same routing constraint as TikTok above —
            // navigate before dispatching the alert so the navigation doesn't clear it.
            logger.error('X OAuth error:', { error, errorDescription, state });
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            dispatch(alertActions.error(errorDescription || t('alertMessages.connectIntegrationFail')));
        } else if (error && state && state.startsWith('li_')) {
            // LinkedIn rejected the authorize step (user_cancelled_authorize, invalid scope, etc).
            // Same navigate-before-alert routing constraint as TikTok/X above.
            logger.error('LinkedIn OAuth error:', { error, errorDescription, state });
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            dispatch(alertActions.error(errorDescription || t('alertMessages.connectIntegrationFail')));
        } else if (error && state && state.startsWith('sq_')) {
            logger.error('Square OAuth error:', { error, errorDescription, state });
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            dispatch(alertActions.error(errorDescription || t('alertMessages.connectIntegrationFail')));
        } else if (error && state && state.startsWith('rc_')) {
            logger.error('RingCentral OAuth error:', { error, errorDescription, state });
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
            dispatch(alertActions.error(errorDescription || t('alertMessages.connectIntegrationFail')));
        } else if (error === GOOGLE_ERROR.CANCEL) {
            navigate(RoutePath.AccountSettingsIntegrations, { replace: true });
        } else if (code && account) {
            // TikTok mints its state with a "tt_" prefix on the backend so we can
            // route the shared callback page without ambiguity.
            if (state && state.startsWith('tt_')) {
                connectTikTokIntegration({ code, state });
            } else if (state && state.startsWith('x_')) {
                connectXIntegration({ code, state });
            } else if (state && state.startsWith('li_')) {
                connectLinkedInIntegration({ code, state });
            } else if (state && state.startsWith('sq_')) {
                connectSquareIntegration({ code, state });
            } else if (state && state.startsWith('rc_')) {
                connectRingCentralIntegration({ code, state });
            } else if (wabaId || state === 'whatsapp') {
                connectWhatsAppIntegration({ code, wabaId });
            } else {
                connectGoogleIntegration(code);
            }
        }
    }, [query, account]);

    const disconnect = (type, integrationId = null) => {
        setDisconnectType(type);
        setDisconnectIntegrationId(integrationId);
        setDisconnectModalOpen(true);
    };

    const reconnect = type => IntegrationMapCb[type]?.notConnectedCb();

    const manageIntegration = (type, integrationId = null) => {
        if (!TwoStepIntegrationConfig[type]) return;
        setManageInitialConnect(false);
        setManageModalIntegrationId(integrationId);
        setManageModalIntegration(type);
    };

    const closeManageModal = () => {
        setManageModalIntegration(null);
        setManageModalIntegrationId(null);
        setManageInitialConnect(false);
    };

    const confirmDisconnect = async () => {
        if (!disconnectType) return;
        try {
            await IntegrationMapCb[disconnectType].apiCall(disconnectIntegrationId);
            dispatch(alertActions.success(t('alertMessages.disconnectIntegrationSuccess')));
            await dispatch(accountActions.get());
            closeManageModal();
        } catch (error) {
            dispatch(alertActions.error(t('apiErrors.google_access_revoke_error')));
        } finally {
            setDisconnectModalOpen(false);
            setDisconnectType(null);
            setDisconnectIntegrationId(null);
        }
    };

    const connect = item => {
        switch (item.setupType) {
            case IntegrationSetupType.ApiDocs: {
                const docsTab = window.open(ExternalRoutes.ApiIntegrationDocs, '_blank');
                if (docsTab) docsTab.opener = null;
                return;
            }
            case IntegrationSetupType.Zapier:
                setZapierOpen(true);
                return;
            case IntegrationSetupType.ContactSales:
                return;
            default:
                IntegrationMapCb[item.id]?.notConnectedCb();
        }
    };

    const modals = (
        <>
            {zapierOpen ? <ZapierModal isOpen={zapierOpen} handleClose={() => setZapierOpen(false)} /> : null}
            {iFoodModalOpen ? <IFoodModal isOpen={iFoodModalOpen} onClose={() => setIFoodModalOpen(false)} /> : null}
            {vtexModalOpen ? (
                <VTEXModal
                    isOpen={vtexModalOpen}
                    onClose={() => setVtexModalOpen(false)}
                    onConnected={() => openManageAfterConnect(AccIntegrationName.VTEX)}
                />
            ) : null}
            {coverManagerModalOpen ? (
                <CoverManagerModal
                    isOpen={coverManagerModalOpen}
                    onClose={() => setCoverManagerModalOpen(false)}
                    onConnected={() => openManageAfterConnect(AccIntegrationName.CoverManager)}
                />
            ) : null}
            {isDisconnectModalOpen && (
                <ModalConfirm
                    isOpen={isDisconnectModalOpen}
                    handleClose={() => setDisconnectModalOpen(false)}
                    title={t('SettingsIntegrations.deleteIntegration')}
                    description={t('SettingsIntegrations.deleteIntegrationMessage')}
                    onPrimaryAction={confirmDisconnect}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                    aboveModal={manageModalIntegration != null}
                />
            )}
            {manageModalIntegration != null && (
                // Two accounts of the same type can expose the same profile key, and rows are seeded
                // once per key, so switching accounts must start from a clean modal.
                <ManageIntegrationModal
                    key={`${manageModalIntegration}-${manageModalIntegrationId}`}
                    integration={manageModalIntegration}
                    integrationId={manageModalIntegrationId}
                    initialConnect={manageInitialConnect}
                    onClose={closeManageModal}
                    onDisconnect={disconnect}
                />
            )}
        </>
    );

    return { connect, disconnect, reconnect, manageIntegration, modals };
};

export default useIntegrationActions;
